import { useEffect, useState } from 'react';
import { config } from '../config/config';

/**
 * Ensures the Google Maps JS API is loaded before map components initialize.
 * Mirrors the loader in ServiceAreaMapDrawer.
 */
export function useGoogleMapsReady(): { isReady: boolean; error: string | null } {
  const [isReady, setIsReady] = useState(() => !!window.google?.maps);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (window.google?.maps) {
      setIsReady(true);
      return;
    }

    const existing = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    ) as HTMLScriptElement | null;

    if (existing) {
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        if (window.google?.maps) {
          clearInterval(poll);
          setIsReady(true);
        } else if (attempts > 100) {
          clearInterval(poll);
          setError('Google Maps failed to load');
        }
      }, 100);
      return () => clearInterval(poll);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsReady(true);
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);
  }, []);

  return { isReady, error };
}
